import React, { useState } from "react";

import { Grid } from "@mui/material";
import { Close as CloseIcon } from "@mui/icons-material";
import { colors } from "@zapalacorp/tokens";
import { format } from "date-fns";
import { zonedTimeToUtc } from "date-fns-tz";
import { isNumber, isBoolean } from "lodash";
import { MemberStaffWithoutTimezoneId } from "Models/members/member.staff";
import { useDropzone } from "react-dropzone";
import { useTranslation } from "react-i18next";

import { Button } from "../../../../../../../../components/Form";
import { Color } from "../../../../../../../../components/Form/Button";
import IconErrorUpload from "../../../../../../../../components/Generic/Icons/IconErrorUpload";
import IconUploadFile from "../../../../../../../../components/Generic/Icons/IconUploadFile";
import { ONE_MB } from "../../../../../../../../constants";
import { useUploadCentreAction } from "../../../../../../../../hooks/useUploadCentreAction";
import useUploadCentreContext from "../../../../../../../../hooks/useUploadCentreContext";
import { Branch } from "../../../../../../../../models/branch";
import { IUploadCentreCreateFleetList } from "../../../../../../../../models/fleet";
import { FleetType } from "../../../../../../../../models/fleet.type";
import { IUploadCentreCreateMaterialList } from "../../../../../../../../models/material";
import { IUploadCentreCreateServiceList } from "../../../../../../../../models/service";
import { IUploadCentreCreateStaffList } from "../../../../../../../../models/staff";
import { User } from "../../../../../../../../models/user";
import { Warehouse } from "../../../../../../../../models/warehouse";
import { convertStringToNumber } from "../../../../../../../../util/convertStringToNumber";
import { UploadCentreColumnType, UploadCentreItemName } from "../../../../../../../../util/enums";
import { regexToMatchWithDateFormatDayMonthYear } from "../../../../../../../../util/regex";
import GET_INPUT_COLUMNS, { ColumnsValues } from "./getInputValidation";
import {
  DropzoneButton,
  TextWrapper,
  DropzoneText,
  DropzoneBoldInfoText,
  DropzoneInfoText,
  Dropzone,
  UploadDocumentModal,
  LoadingContainer,
  LoadingTitle,
  LoadingProgress,
  LoadingDiv,
  CancelButtonWrapper,
  ModalHeaderTitleWrapper,
  CloseModalButtonWrapper,
  ModalHeaderWrapper,
  ErrorLine,
  RejectedUploadTextWrapper,
} from "./styles";

type CSVValueProps = {
  [x: string]: string | number | unknown[] | boolean | undefined;
};

type InputUploadProps = {
  isOpenUploadModal: boolean;
  isOpenUploadErrorModal: boolean;
  setIsOpenUploadModal: React.Dispatch<React.SetStateAction<boolean>>;
  setIsOpenUploadErrorModal: React.Dispatch<React.SetStateAction<boolean>>;
  columnType: ColumnsValues;
  title: string;
  handleCloseModal: () => void;
  itemLabelId: number;
  currentUser?: User;
  onRequestSuccess: () => void;
};

type ICheckIfTheValueIsValid = {
  hasError: boolean;
  data: CSVValueProps[];
};

type CheckIfCodeIsRelated = {
  hasRelationError: boolean;
  data: string[];
};

const checkIfCodeIsRelated = (data: string[], warehouse: Warehouse): CheckIfCodeIsRelated => {
  const branchCodes = new Set(warehouse?.branches?.map((branch) => branch.code?.trim()));

  if (data.every((code) => branchCodes.has(code.trim()))) {
    const branchesAreRelated = data.map((value) => value.trim());

    return { hasRelationError: false, data: branchesAreRelated };
  }

  const notRelatedBraches = data.filter((code) => !branchCodes.has(code.trim()));

  return { hasRelationError: true, data: notRelatedBraches };
};

export const InputModal = ({
  isOpenUploadModal,
  setIsOpenUploadModal,
  setIsOpenUploadErrorModal,
  title,
  columnType,
  handleCloseModal,
  itemLabelId,
  currentUser,
  onRequestSuccess,
}: InputUploadProps) => {
  const { t: translate } = useTranslation();

  const COLUMNS = GET_INPUT_COLUMNS(translate);

  const { setUploadedDataCSV, setCsvName, setUploadedDataError } = useUploadCentreContext();

  const {
    warehouseList,
    servicesCodes,
    materialsCodes,
    branchList,
    fleetTypes,
    updateFleetList,
    updateMaterialList,
    updateMemberList,
    updateServiceList,
    isUpdateFleetListLoading,
    isUpdateMaterialListLoading,
    isUpdateServiceListLoading,
    isUpdateStaffListLoading,
    reloadMaterialsList,
    reloadServicesList,
  } = useUploadCentreAction();

  const [isUploadRejected, setIsUplaodRejected] = useState<boolean>(false);

  const isUpdateLoading: boolean =
    isUpdateFleetListLoading ||
    isUpdateMaterialListLoading ||
    isUpdateServiceListLoading ||
    isUpdateStaffListLoading;

  const buttons = (
    <CancelButtonWrapper>
      <Button
        variant="text"
        data-cy="button-cancel"
        onClick={() => {
          setIsOpenUploadModal(false);
        }}
        customColor={Color.GRAY}
      >
        {translate("cancel")}
      </Button>
    </CancelButtonWrapper>
  );

  const modalHeaderComponent = (
    <ModalHeaderWrapper container>
      <Grid item justifyContent="flex-start" xs={6}>
        <ModalHeaderTitleWrapper>{`${translate("upload")} "${translate(
          title
        )}"`}</ModalHeaderTitleWrapper>
      </Grid>
      <CloseModalButtonWrapper item xs={6}>
        <CloseIcon fontSize="medium" onClick={() => handleCloseModal()} />
      </CloseModalButtonWrapper>
    </ModalHeaderWrapper>
  );

  function getDataByProperty<T>(list: T[], listProperty: keyof T, value: string): T | null {
    if (value) {
      return list?.find((data) => String(data[listProperty]) === value) || null;
    }

    return null;
  }

  const renderDuplicateCodeError = (columnType?: ColumnsValues) => {
    switch (columnType) {
      case UploadCentreItemName.SERVICE:
        return translate("serviceCodeAlreadyExistsPleaseAssignANewCode");
      case UploadCentreItemName.MATERIAL:
        return translate("materialCodeAlreadyExistsPleaseAssignANewCode");
      default:
        return translate("codeAlreadyExistsPleaseAssignANewCode");
    }
  };

  const valueIsMatchWithDateFormat = (value: string): boolean => {
    const formatedDate = format(new Date(value), "dd/MM/yyyy");

    const dateIsValid = formatedDate.match(regexToMatchWithDateFormatDayMonthYear);

    return !!dateIsValid;
  };

  const convertValueToDateFormat = (value: string): string => {
    const dateIsValid = value.match(regexToMatchWithDateFormatDayMonthYear);

    if (!dateIsValid) return value;

    const [date] = dateIsValid;

    const [dayString, monthString, yearString] = date.split("/");

    const day = Number(dayString);
    const month = Number(monthString) - 1;
    const year = Number(yearString);

    const newDate = zonedTimeToUtc(
      new Date(year, month, day),
      Intl.DateTimeFormat().resolvedOptions().timeZone
    ).toISOString();

    return newDate;
  };

  const convertValueToBoolean = (value: string): boolean | string => {
    if (!value) return false;

    if (value.toLocaleLowerCase() === "x") return true;

    return value;
  };

  const checkIfTheValueIsValid = async (
    columnName: keyof typeof COLUMNS,
    data: CSVValueProps[]
  ): Promise<ICheckIfTheValueIsValid> => {
    if (!data || data?.length === 0) {
      const textTemplateIsInvalid = translate("csvTemplateIsInvalid");

      setUploadedDataError([textTemplateIsInvalid]);

      return { hasError: true, data: [] };
    }

    const error: string[] = [];
    let hasError: boolean = false;
    const newData: typeof data = [];
    const codesInCSV: string[] = [];

    for (const [index, row] of Array.from(data.entries())) {
      let warehouse: Warehouse | null = null;
      // eslint-disable-next-line prefer-const
      let relatedBranch: Branch | null = null;
      let relatedBranches: Branch[] | null = [] as Branch[];

      for (const key in row) {
        if (row.hasOwnProperty.call(row, key)) {
          const column = COLUMNS[columnName].find((column) => column.key === key);

          if (!column) {
            setUploadedDataError([translate("csvTemplateIsInvalid")]);

            return { hasError: true, data: [] };
          }

          if (!row[key] && row[key] !== 0 && column.isRequired) {
            const stringError = `${translate("line")} ${index + 1} - ${
              column?.label ? `${column.label} - ` : ""
            } ${translate("isRequired")}`;

            error.push(stringError);
            hasError = true;
          }

          if (column.type === UploadCentreColumnType.WAREHOUSE_CODE) {
            warehouse = getDataByProperty(warehouseList || [], "code", String(row[key]));

            if (!warehouse) {
              const warehouseRelationError = `${translate("line")} ${index + 1} - ${
                column?.label ? `${column.label} - ` : ""
              } ${translate("warehouseNotFound")}`;

              error.push(warehouseRelationError);
              hasError = true;
            }
          }

          if (warehouse) {
            if (column.type === UploadCentreColumnType.BRANCH_CODE) {
              const branchesCode = String(row[key]).split("/");

              if (branchesCode && branchesCode.length > 0) {
                const { data, hasRelationError } = checkIfCodeIsRelated(branchesCode, warehouse);

                if (!hasRelationError) {
                  const getBranchesByCodes = (codes: string[], branches: Branch[]): Branch[] => {
                    const codesSet = new Set(
                      codes.filter((code): code is string => code !== undefined)
                    );

                    return branches.filter(
                      (branch) => branch.code !== undefined && codesSet.has(branch.code)
                    );
                  };

                  const branches = getBranchesByCodes(data, branchList);

                  relatedBranches = branches;
                } else {
                  const branchCodeError = (data: string) => {
                    return `${translate("line")} ${index + 1} - ${
                      column?.label ? `${column.label} - ` : ""
                    } ${translate("theBranch")} ${data} ${translate(
                      "isNotRelatedToInformedWarehouse"
                    )}`;
                  };

                  const errors = data.map((code) => branchCodeError(code));

                  error.push(...errors);
                  hasError = true;
                }
              } else {
                const branchCodeError = `${translate("line")} ${index + 1} - ${
                  column?.label ? `${column.label} - ` : ""
                } ${translate("codeNotFound")}`;

                error.push(branchCodeError);
                hasError = true;
              }
            }
          }

          if (columnType === UploadCentreItemName.SERVICE) {
            if (column.type === UploadCentreColumnType.CODE) {
              const codeValueExists = getDataByProperty(
                servicesCodes || [],
                "code",
                String(row[key])
              );

              if (codeValueExists) {
                const codeAlreadyExistsError = `${translate("line")} ${index + 1} - ${
                  column?.label ? `${column.label} - ` : ""
                } ${renderDuplicateCodeError(columnType)}`;

                error.push(codeAlreadyExistsError);
                hasError = true;
              }

              if (codesInCSV.includes(String(row[key]))) {
                const codeIsDuplicated = `${translate("line")} ${
                  index + 1
                } - ${`${row[key]} - `} ${translate("codeIsDuplicated")}`;

                error.push(codeIsDuplicated);
                hasError = true;
              } else {
                codesInCSV.push(String(row[key]));
              }
            }
          }

          if (columnType === UploadCentreItemName.STAFF) {
            if (column.type === UploadCentreColumnType.BRANCH_CODE) {
              const branch = getDataByProperty(branchList || [], "code", String(row[key]));

              if (!branch && branch === null) {
                const branchCodeError = `${translate("line")} ${index + 1} - ${
                  column?.label ? `${column.label} - ` : ""
                } ${translate("codeNotFound")}`;

                error.push(branchCodeError);
                hasError = true;
              }

              if (branch) relatedBranch = branch;
            }
          }

          if (columnType === UploadCentreItemName.MATERIAL) {
            if (column.type === UploadCentreColumnType.CODE) {
              const codeValueExists = getDataByProperty(
                materialsCodes || [],
                "code",
                String(row[key])
              );

              if (codeValueExists) {
                const codeAlreadyExistsError = `${translate("line")} ${index + 1} - ${
                  column?.label ? `${column.label} - ` : ""
                } ${renderDuplicateCodeError(columnType)}`;

                error.push(codeAlreadyExistsError);
                hasError = true;
              }

              if (codesInCSV.includes(String(row[key]))) {
                const codeIsDuplicated = `${translate("line")} ${
                  index + 1
                } - ${`${row[key]} - `} ${translate("codeIsDuplicated")}`;

                error.push(codeIsDuplicated);
                hasError = true;
              } else {
                codesInCSV.push(String(row[key]));
              }
            }
          }

          if (column.type === "fleetId" && !isNumber(row[key])) {
            const fleetIdError = `${translate("line")} ${index + 1} - ${
              column?.label ? `${column.label} - ` : ""
            } ${translate("mustBeOneOfTheValidOptions")}`;

            error.push(fleetIdError);
            hasError = true;
          }

          if (column.type === "boolean" && !isBoolean(row[key])) {
            const booleanError = `${translate("line")} ${index + 1} - ${
              column?.label ? `${column.label.replace(/[^a-zA-Z?{1}() ]/g, "")} - ` : ""
            } ${translate("mustBeFilledInWithXOrLeftEmpty")}`;

            error.push(booleanError);
            hasError = true;
          }

          if (column.type === "number" && row[key]) {
            const rawValue = String(row[key]).replace(/"|,/g, (value: string) => {
              if (value === '"') {
                return " ";
              }

              return ".";
            });

            const keyValue = rawValue.trim().split(/\.|,/gi);

            const rowKeyValue = (decimalValue?: string) => {
              if (decimalValue) {
                const value = Number(`${keyValue.join("")}.${decimalValue}`).toFixed(
                  decimalValue.length
                );

                return Number(value);
              }

              return Number(keyValue.join(""));
            };

            if (keyValue.length > 1) {
              const lastKeyValue = keyValue[keyValue.length - 1];

              keyValue[keyValue.length - 1] = "";

              if (!isNumber(rowKeyValue(lastKeyValue))) {
                const numberError = `${translate("line")} ${index + 1} - ${
                  column?.label ? `${column.label} - ` : ""
                } ${translate("mustBeANumber")}`;

                error.push(numberError);
                hasError = true;
              } else {
                row[key] = rowKeyValue(lastKeyValue);
              }
            } else if (!isNumber(rowKeyValue())) {
              const numberError = `${translate("line")} ${index + 1} - ${
                column?.label ? `${column.label} - ` : ""
              } ${translate("mustBeANumber")}`;

              error.push(numberError);
              hasError = true;
            } else {
              row[key] = rowKeyValue();
            }
          }

          if (
            column.type === "date" &&
            row[key] &&
            !valueIsMatchWithDateFormat(row[key] as string)
          ) {
            const numberError = `${translate("line")} ${index + 1} - ${
              column?.label ? `${column.label} - ` : ""
            } ${translate("mustBeADateWithTheFormatDdMmYyyy")}`;

            error.push(numberError);

            hasError = true;
          }
        }
      }

      const allRelatedBranches: Branch[] | undefined = relatedBranches
        ? relatedBranches.filter((branch): branch is Branch => branch.id !== undefined)
        : undefined;

      if (columnType === UploadCentreItemName.FLEET) {
        newData.push({
          ...row,
          warehouseId: warehouse?.id,
          branches: allRelatedBranches,
        });
      } else if (columnType === UploadCentreItemName.STAFF) {
        newData.push({
          ...row,
          warehouseId: warehouse?.id,
          branchId: relatedBranch?.id,
          branches: relatedBranch?.id ? [relatedBranch] : [],
        });
      } else {
        newData.push({ ...row, warehouseId: warehouse?.id, branchId: relatedBranch?.id });
      }
    }
    setUploadedDataError(error);

    return { hasError, data: newData };
  };

  const mountCSVToArray = async (columnName: keyof typeof COLUMNS, csvData?: string) => {
    if (!csvData) return;
    const rows = csvData.split("\n").map((row) => row.replace("\r", ""));

    if (!rows || rows.length <= 1) return;

    const csvArray: CSVValueProps[] = [];
    let fleetTypeList: FleetType[] | undefined;

    if (columnType === UploadCentreItemName.FLEET) {
      fleetTypeList = fleetTypes;
    }

    const columnsReceivedFromCSV = rows[0].split(/,(?=(?:(?:[^"]*"){2})*[^"]*$)/);
    const columnsExpectedFromCSV = COLUMNS[columnName].map((column) => column.csvColumnName);

    if (
      columnsExpectedFromCSV.every(
        (columnExpected, index) => columnsReceivedFromCSV[index].trim() === columnExpected?.trim()
      )
    ) {
      for (let index = 1, { length } = rows; index < length; index += 1) {
        const row = rows[index];
        const content = row.split(/,(?=(?:(?:[^"]*"){2})*[^"]*$)/);

        if (content && content.length === COLUMNS[columnName].length) {
          const csvObject: CSVValueProps = {};

          for (const [index, column] of Array.from(COLUMNS[columnName].entries())) {
            const value = content[index]?.trim();

            if (column.type === "fleetId") {
              const fleetId = fleetTypeList?.find(
                (fleetType) => fleetType.value.toLocaleLowerCase() === value?.toLocaleLowerCase()
              )?.id;

              csvObject[column.key] = fleetId;
            } else if (column.type === "boolean") {
              csvObject[column.key] = convertValueToBoolean(value);
            } else if (column.type === "number") {
              csvObject[column.key] = value ? convertStringToNumber(value) : value;
            } else if (column.type === "date") {
              csvObject[column.key] = value ? convertValueToDateFormat(value) : value;
            } else {
              csvObject[column.key] = value ? value.replace(/"/g, "") : "";
            }
          }

          csvArray.push(csvObject);
        }
      }
    } else {
      setUploadedDataError([translate("csvTemplateIsInvalid")]);
    }

    const { data, hasError } = await checkIfTheValueIsValid(columnName, csvArray);

    if (hasError || data.length < 1) {
      setIsOpenUploadModal(false);
      setIsOpenUploadErrorModal(true);

      return;
    }

    setUploadedDataError([]);
    setUploadedDataCSV(data);
    const newValue = data.map((csvValue) => {
      return { ...csvValue, companyId: currentUser?.companyId };
    });

    switch (columnType) {
      case UploadCentreItemName.FLEET: {
        updateFleetList(newValue as IUploadCentreCreateFleetList[], itemLabelId, (fleets) => {
          if (fleets?.length) {
            onRequestSuccess();

            return;
          }

          setIsOpenUploadModal(false);
        });

        return;
      }
      case UploadCentreItemName.MATERIAL: {
        updateMaterialList(
          newValue as IUploadCentreCreateMaterialList[],
          itemLabelId,
          (materials) => {
            if (materials?.length) {
              onRequestSuccess();
              reloadMaterialsList();

              return;
            }

            setIsOpenUploadModal(false);
          }
        );

        return;
      }
      case UploadCentreItemName.SERVICE: {
        updateServiceList(newValue as IUploadCentreCreateServiceList[], itemLabelId, (service) => {
          if (service?.length) {
            onRequestSuccess();
            reloadServicesList();

            return;
          }

          setIsOpenUploadModal(false);
        });

        return;
      }
      case UploadCentreItemName.STAFF: {
        const staffsParsed = newValue as IUploadCentreCreateStaffList[];
        const membersParsed = staffsParsed.map(
          (staff) =>
            ({
              ...staff,
              residencyStatus: "Unknown",
              status: "Active",
            } as MemberStaffWithoutTimezoneId)
        );

        updateMemberList(membersParsed, itemLabelId, (members) => {
          if (members?.length) {
            onRequestSuccess();

            return;
          }

          setIsOpenUploadModal(false);
        });

        return;
      }
      default: {
        setIsOpenUploadModal(false);
      }
    }
  };

  const handleChangeFile = async (files: File[]) => {
    setIsUplaodRejected(false);
    if (files && files?.length > 0) {
      const csv = files[0];

      setCsvName(csv.name.replace(".csv", ""));

      const reader = new FileReader();

      if (columnType) {
        reader.onloadend = () => {
          mountCSVToArray(columnType as keyof typeof COLUMNS, reader.result?.toString());
        };
        reader.readAsText(csv);
      }
    }
  };

  const { getRootProps, getInputProps } = useDropzone({
    onDropAccepted: handleChangeFile,
    onDropRejected() {
      setIsUplaodRejected(true);
    },
    maxFiles: 1,
    accept: [".csv", "text/csv"],
    maxSize: ONE_MB * 2,
  });

  const DropzoneInput = () => {
    return (
      <Dropzone {...getRootProps({ className: "dropzone" })}>
        <input {...getInputProps()} />
        <DropzoneButton>
          <IconUploadFile height={24} width={24} color={colors.brandBlue500} />
          <TextWrapper>
            <DropzoneText>{translate("dragOrClickToUploadYourFile")}</DropzoneText>
            <TextWrapper gap="0px">
              <DropzoneBoldInfoText>
                {translate("maxSize")}:{" "}
                <DropzoneInfoText>{` ${translate("2Mb")}`}</DropzoneInfoText>
              </DropzoneBoldInfoText>
              <DropzoneBoldInfoText>
                {translate("formats")}:{" "}
                <DropzoneInfoText>{` ${translate("csv")}`}</DropzoneInfoText>
              </DropzoneBoldInfoText>
            </TextWrapper>
          </TextWrapper>
        </DropzoneButton>
      </Dropzone>
    );
  };

  const renderLoading = () => {
    return (
      <LoadingDiv>
        <LoadingContainer>
          <LoadingTitle>{translate("weAreLoadingTheUploadedContentPleaseWait")}</LoadingTitle>
          <LoadingProgress />
        </LoadingContainer>
      </LoadingDiv>
    );
  };

  return (
    <>
      <UploadDocumentModal
        title={`${translate("upload")} "${translate(title)}"`}
        open={isOpenUploadModal}
        customHeaderComponent={modalHeaderComponent}
        customRightButtons={buttons}
      >
        {isUpdateLoading ? renderLoading() : DropzoneInput()}
        {isUploadRejected && (
          <RejectedUploadTextWrapper>
            <IconErrorUpload height={20} width={20} color={colors.error600} />
            <ErrorLine>{translate("theFileToUploadMustBeInCSVFormat")}</ErrorLine>
          </RejectedUploadTextWrapper>
        )}
      </UploadDocumentModal>
    </>
  );
};
